/**
 * useFocusTrap
 *
 * Traps keyboard focus inside a container while it is "active" (e.g. a
 * modal dialog). Implements the focus-management half of the WAI-ARIA
 * dialog pattern:
 *
 *   - On activation, moves focus to `initialFocus` (or the first
 *     focusable descendant) and remembers what was focused before
 *     so we can restore it on deactivation.
 *   - Tab / Shift+Tab at the boundary wraps to the other end of the
 *     focusable list inside the container, so focus never escapes
 *     to the (visually obscured) page behind the dialog.
 *   - On deactivation, restores focus to the originally-focused element.
 *
 * Why custom rather than Fluent `Dialog`/Tabster: the palette is a
 * non-Fluent custom modal built on `<div role="dialog">` for the
 * editorial look the breadcrumb research called for. We still need
 * dialog-level focus discipline; this hook provides exactly that
 * without forcing a wholesale switch to Fluent's Dialog primitive.
 */

import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'audio[controls]',
  'video[controls]',
  '[contenteditable=""]',
  '[contenteditable="true"]',
].join(',');

interface UseFocusTrapOptions {
  /** Whether the trap is currently active (typically tied to "isOpen"). */
  active: boolean;
  /** Container the trap operates inside. */
  containerRef: RefObject<HTMLElement | null>;
  /**
   * Optional explicit initial focus target. Falls back to the first
   * focusable descendant of the container.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

function isVisible(el: HTMLElement): boolean {
  // We previously checked `getClientRects().length > 0`, but jsdom doesn't
  // compute layout so every element returns `[]` there — making the trap
  // un-testable. `display: none` and `visibility: hidden` are the cases we
  // actually need to exclude, and `getComputedStyle` reports both reliably
  // in real browsers and jsdom alike. Elements with zero-size boxes (which
  // are still considered focusable per the HTML spec) are now correctly
  // included.
  const style = el.ownerDocument.defaultView?.getComputedStyle(el);
  if (style && (style.visibility === 'hidden' || style.display === 'none')) {
    return false;
  }
  return true;
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.getAttribute('aria-hidden') !== 'true' &&
      isVisible(el),
  );
}

export function useFocusTrap({
  active,
  containerRef,
  initialFocusRef,
}: UseFocusTrapOptions): void {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    // Move focus into the trap on the next frame so layout (and any
    // late-mounted children) has settled.
    const focusFrame = requestAnimationFrame(() => {
      const target =
        initialFocusRef?.current ?? getFocusable(container)[0] ?? container;
      target.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const focusables = getFocusable(container);
      if (focusables.length === 0) {
        // Empty trap — keep focus pinned to the container itself.
        event.preventDefault();
        container.focus();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const activeEl = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last || !container.contains(activeEl)) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus on close in a microtask so we don't fight a sibling
      // effect that might also be unmounting/blurring on the same teardown
      // tick. By the time the microtask runs, sibling cleanups have settled
      // and our `.focus()` is the last word on the active element.
      const previous = previouslyFocusedRef.current;
      previouslyFocusedRef.current = null;
      if (previous && typeof previous.focus === 'function') {
        queueMicrotask(() => {
          if (!previous.isConnected) return;
          // Suppress scroll-jump from focus restoration when we already
          // scrolled past the previous element.
          try {
            previous.focus({ preventScroll: true });
          } catch {
            previous.focus();
          }
        });
      }
    };
  }, [active, containerRef, initialFocusRef]);
}
